/**
 * Hook: liefert Maps für name → id-Präfix bei Warengruppe und Artikel.
 *
 * Quelle: /dimensions Response — Backend gibt das Label im Format
 * "{id} — {name}" zurück, hier extrahieren wir die ID wieder.
 *
 * Wird in FilterChips und Pivot verwendet, um konsistent
 * "{id} — {name}" anzuzeigen.
 */

import { useQuery } from '@tanstack/react-query';
import { api } from './api.ts';

interface DimLabelMaps {
  warengruppe: Map<string, string>;
  artikel: Map<string, string>;
}

function buildMap(values: Array<{ value: string | number; label: string }> | undefined) {
  const map = new Map<string, string>();
  if (!values) return map;
  for (const v of values) {
    const lbl = String(v.label);
    const val = String(v.value);
    // Label-Format: "{id} — {name}" → wir parsen ID, sofern Name == value
    const m = /^(\S+)\s+—\s+(.+)$/.exec(lbl);
    if (m && m[2] === val) map.set(val, m[1]);
  }
  return map;
}

export function useDimLabelMaps(): DimLabelMaps {
  const { data } = useQuery({ queryKey: ['dimensions'], queryFn: api.dimensions });
  const wgDim = data?.find((d) => d.dimension === 'warengruppe_name');
  const artDim = data?.find((d) => d.dimension === 'artikel_bezeichnung');
  return {
    warengruppe: buildMap(wgDim?.values),
    artikel: buildMap(artDim?.values),
  };
}

/** Backwards-compat alias — manche Caller importieren noch useWgIdMap. */
export function useWgIdMap(): Map<string, string> {
  return useDimLabelMaps().warengruppe;
}

/** Formatiert "{id} — {name}" wenn id bekannt, sonst nur name. */
export function fmtWg(name: string, idMap: Map<string, string>): string {
  const id = idMap.get(name);
  return id ? `${id} — ${name}` : name;
}
